import React from 'react';
import { useQuery } from 'react-query';
import { UserHttpService } from '../service';
import {
    Paper,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    Box,
    CircularProgress,
    Chip
} from '@mui/material';
import { Utils } from 'core/utils';
import {
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';

function LastOnlineUsersWidget() {
    const { data, isLoading } = useQuery(
        'lastOnlineUsers',
        () => UserHttpService.loadLastOnlineUsers(),
        {
            refetchInterval: 30000, // Refetch every 30 seconds
            staleTime: 10000 // Consider data stale after 10 seconds
        }
    );

    const users = data?.results || data || [];

    const getUserName = (user) => {
        if (user.profile) {
            return getProfileDisplayName(user.profile, user) || user.username || user.email;
        }
        return user.username || user.email;
    };

    const getTimeAgo = (lastOnline) => {
        if (!lastOnline) return 'Unknown';
        const date = new Date(lastOnline);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        return date.toLocaleDateString();
    };

    const isOnline = (lastOnline) => {
        if (!lastOnline) return false;
        const date = new Date(lastOnline);
        const now = new Date();
        const diffMs = now - date;
        return diffMs < 300000; // Online if last seen less than 5 minutes ago
    };

    if (isLoading) {
        return (
            <Paper elevation={0} sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h6" fontWeight={650} sx={{ mb: 1 }}>
                    Last Online Users
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="h6" fontWeight={650} sx={{ mb: 1 }}>
                Last Online Users
            </Typography>
            {users.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No users found
                </Typography>
            ) : (
                <List sx={{ p: 0 }}>
                    {users.map((user, index) => {
                        const online = isOnline(user.last_online);
                        const displayName = getUserName(user);
                        const avatarSrc = getProfileAvatarUrl(user.profile);
                        return (
                            <ListItem
                                key={user.id}
                                sx={{
                                    px: 0,
                                    py: 1.1,
                                    borderBottom: index < users.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider'
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar
                                        src={avatarSrc || undefined}
                                        alt={displayName}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            fontWeight: 700,
                                            bgcolor: nameToAvatarColor(displayName),
                                        }}
                                    >
                                        {getProfileAvatarLetter(user.profile, user)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {displayName}
                                            </Typography>
                                            {online && (
                                                <Chip
                                                    label="Online"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'transparent' }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {getTimeAgo(user.last_online)}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Paper>
    );
}

export default LastOnlineUsersWidget;
